import { coordsOf, distanceKm, travelMinutes } from 'helper/geo';
import { addDays, dayLabel, fromIsoDay, isoDay, shortDayLabel } from 'helper/timeLabels';

/**
 * The planning of the sales team, as on the prototype: the week from Monday,
 * the appointments side by side when they overlap, the visits to place and
 * the tour proposed for each sector. Pure.
 */

// The schedule of the sales rep until it is set in their settings: working
// from home on Wednesday (`getDay`), on duty from 8 to 16.
export const MY_SCHEDULE = { remoteDays: [3], shift: [8, 16] };
export const FIRST_HOUR = 8;
export const LAST_HOUR = 18;

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

export const startOfDay = (date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};
export const sameDay = (a, b) => isoDay(new Date(a)) === isoDay(new Date(b));
export const mondayOf = (date) => addDays(startOfDay(date), -((new Date(date).getDay() + 6) % 7));

export const isoWeek = (date) => {
  const day = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  day.setUTCDate(day.getUTCDate() + 4 - (day.getUTCDay() || 7));
  return Math.ceil(((day - new Date(Date.UTC(day.getUTCFullYear(), 0, 1))) / 86400000 + 1) / 7);
};

// « 19 – 25 octobre », or « 26 octobre – 1 novembre ».
export const weekRange = (monday) => {
  const sunday = addDays(monday, 6);
  return monday.getMonth() === sunday.getMonth()
    ? `${monday.getDate()} – ${sunday.getDate()} ${MONTHS[sunday.getMonth()]}`
    : `${monday.getDate()} ${MONTHS[monday.getMonth()]} – ${sunday.getDate()} ${MONTHS[sunday.getMonth()]}`;
};

// « Mercredi 21 octobre »
export const longDay = (date) => `${DAYS[date.getDay()].charAt(0).toUpperCase()}${DAYS[date.getDay()].slice(1)} ${date.getDate()} ${MONTHS[date.getMonth()]}`;
export const weekdayName = (date) => DAYS[date.getDay()];
export const partOfDay = (date) => (date.getHours() < 12 ? 'morning' : 'afternoon');

// The days shown: the whole week, Saturday and Sunday included, or one day.
export const daysOf = (mode, anchor) =>
  mode === 'week' ? Array.from({ length: 7 }, (_, index) => addDays(mondayOf(anchor), index)) : [startOfDay(anchor)];

/**
 * An appointment held in the row of the day rather than in the grid: a whole
 * day, or one whose start and end fall on different local days.
 */
export const spansDays = (event) => Boolean(event.allDay) || isoDay(new Date(event.startsAt)) !== isoDay(new Date(event.endsAt));

/**
 * The local days an appointment covers, « 2026-10-21 » each. A whole day runs
 * from startDay to endDay, endDay excluded as in Google; one with hours, from
 * the day of its start to the day of its end, which an end at 0:00 leaves out.
 */
export const appointmentDays = (event) => {
  let first;
  let last;
  if (event.allDay && event.startDay && event.endDay) {
    first = fromIsoDay(event.startDay);
    last = addDays(fromIsoDay(event.endDay), -1);
  } else {
    const start = new Date(event.startsAt);
    const end = new Date(event.endsAt);
    first = startOfDay(start);
    last = startOfDay(end);
    if (+end === +last && last > first) last = addDays(last, -1);
  }
  const days = [];
  for (let day = first; isoDay(day) <= isoDay(last); day = addDays(day, 1)) days.push(isoDay(day));
  return days.length ? days : [isoDay(first)];
};

/**
 * The hours the grid shows: at least FIRST_HOUR to LAST_HOUR, stretched to the
 * full hour of the earliest start and of the latest end of `events`.
 */
export const gridHours = (events) => {
  let first = FIRST_HOUR;
  let last = LAST_HOUR;
  events.forEach((event) => {
    const start = new Date(event.startsAt);
    const end = new Date(event.endsAt);
    first = Math.min(first, start.getHours());
    last = Math.max(last, end.getHours() + (end.getMinutes() || end.getSeconds() ? 1 : 0));
  });
  return { first: Math.max(0, first), last: Math.min(24, last) };
};

/**
 * Items that overlap go side by side: each gets its column (`col`) out of how
 * many its group needs (`cols`).
 */
export const lanes = (items) => {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  const out = [];
  let group = [];
  let groupEnd = 0;
  const flush = () => {
    const columns = [];
    const placed = group.map((item) => {
      let column = columns.findIndex((end) => end <= item.start);
      if (column === -1) {
        column = columns.length;
        columns.push(0);
      }
      columns[column] = item.end;
      return { ...item, col: column };
    });
    placed.forEach((item) => out.push({ ...item, cols: columns.length }));
    group = [];
  };
  sorted.forEach((item) => {
    if (group.length && item.start >= groupEnd) flush();
    group.push(item);
    groupEnd = Math.max(groupEnd, +item.end);
  });
  if (group.length) flush();
  return out;
};

const overlaps = (events, start, end) => events.some((event) => new Date(event.startsAt) < end && new Date(event.endsAt) > start);
const isWorkday = (date) => date.getDay() !== 0 && date.getDay() !== 6;
const BLOCKS = [
  [9, 12],
  [14, 17],
];

// Visits in the order of the road, from the most urgent one: the nearest next.
export const orderByRoute = (visits) => {
  if (visits.length < 2) return visits;
  const rest = [...visits].sort((a, b) => a.dueBy.localeCompare(b.dueBy));
  const out = [rest.shift()];
  while (rest.length) {
    const last = coordsOf(out[out.length - 1].company);
    let best = 0;
    let bestKm = Infinity;
    rest.forEach((visit, index) => {
      const km = distanceKm(last, coordsOf(visit.company)) ?? 999;
      if (km < bestKm) {
        bestKm = km;
        best = index;
      }
    });
    out.push(rest.splice(best, 1)[0]);
  }
  return out;
};

export const routeLegs = (visits) =>
  visits.map((visit, index) => (index ? travelMinutes(distanceKm(coordsOf(visits[index - 1].company), coordsOf(visit.company))) : 0));

export const routeKm = (visits) =>
  visits.reduce((sum, visit, index) => sum + (index ? distanceKm(coordsOf(visits[index - 1].company), coordsOf(visit.company)) ?? 0 : 0), 0);

// The first free start, to the quarter, of a morning or an afternoon of a
// working day between two days, for a tour of `need` minutes.
const firstStart = (events, need, from, to, now) => {
  for (let day = from; day <= to; day = addDays(day, 1)) {
    if (!isWorkday(day)) continue;
    for (const [first, last] of BLOCKS) {
      for (let minutes = first * 60; minutes + need <= last * 60; minutes += 15) {
        const start = new Date(day);
        start.setHours(0, minutes, 0, 0);
        const end = new Date(start.getTime() + need * 60000);
        if (start >= now && !overlaps(events, start, end)) return { start, end };
      }
    }
  }
  return null;
};

const tourOf = (visits, events, now) => {
  const ordered = orderByRoute(visits);
  const legs = routeLegs(ordered);
  const need = ordered.reduce((sum, visit) => sum + (visit.durationMin || 45), 0) + legs.reduce((sum, leg) => sum + leg, 0);
  // Never after the nearest deadline; a deadline passed: within five days.
  const today = startOfDay(now);
  const due = fromIsoDay(ordered.map((visit) => visit.dueBy).sort()[0]);
  const last = startOfDay(due) < today ? addDays(today, 5) : startOfDay(due);
  const slot = firstStart(events, need, today, last, now);
  return slot && { ...slot, ordered, legs, need };
};

/**
 * The tour of a sector: the first slot where all its visits fit before the
 * nearest of their deadlines; else at least the most urgent ones.
 */
export const proposeTour = (visits, events, now = new Date()) => {
  if (!visits.length) return null;
  const tour = tourOf(visits, events, now);
  if (tour || visits.length < 2) return tour;
  const first = visits.map((visit) => visit.dueBy).sort()[0];
  const urgent = visits.filter((visit) => visit.dueBy === first);
  return urgent.length < visits.length ? tourOf(urgent, events, now) : null;
};

// The first free slot of a day, from 8:30, for a visit.
export const firstFreeSlot = (day, events, minutes) => {
  const ofDay = events.filter((event) => sameDay(event.startsAt, day));
  for (let at = 8 * 60 + 30; at + minutes <= 17 * 60; at += 15) {
    const start = new Date(day);
    start.setHours(0, at, 0, 0);
    if (!overlaps(ofDay, start, new Date(start.getTime() + minutes * 60000))) return start;
  }
  return null;
};

// Unbreakable spaces: « 16 h 58 » never splits at the end of a line.
const hour = (date) => `${date.getHours()} h${date.getMinutes() ? ` ${String(date.getMinutes()).padStart(2, '0')}` : ''}`;
export const slotLabel = (start, end, text) => text('slot', { day: dayLabel(start), from: hour(start), to: hour(end) });

// When a visit has to be placed by, urgent from tomorrow.
export const deadlineOf = (visit, now, text) => {
  const today = isoDay(now);
  const tomorrow = isoDay(addDays(now, 1));
  if (visit.dueBy <= tomorrow) {
    const key = visit.dueBy < today ? 'dueLate' : visit.dueBy === today ? 'dueTonight' : 'dueTomorrow';
    return { text: text(key), urgent: true };
  }
  return { text: text('dueBy', { day: shortDayLabel(visit.dueBy) }), urgent: false };
};

/**
 * « Ma semaine »: the appointments held, the tasks done, the calls answered,
 * and the road between the appointments of each day.
 */
export const weekStats = (data, monday) => {
  let road = 0;
  for (let index = 0; index < 7; index += 1) {
    const day = addDays(monday, index);
    const located = data.events
      .filter((event) => sameDay(event.startsAt, day) && coordsOf(event.company))
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
    for (let next = 1; next < located.length; next += 1) {
      road += travelMinutes(distanceKm(coordsOf(located[next - 1].company), coordsOf(located[next].company)));
    }
  }
  const end = addDays(monday, 7);
  const week = data.events.filter((event) => new Date(event.startsAt) >= monday && new Date(event.startsAt) < end && event.source !== 'google');
  return {
    events: { done: week.filter((event) => event.status === 'done').length, total: week.length },
    tasks: data.weekTasks,
    calls: data.weekCalls,
    road,
  };
};

// « 1 h 05 », « 45 min »: a length of road.
export const roadLabel = (minutes) =>
  minutes >= 60 ? `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}` : `${minutes} min`;
